import type { StatusTone } from './StatusBadge';

/**
 * Each map mirrors the CHECK-constrained enum for that column in
 * backend/migrations/0001_initial_schema.sql. The same word can mean a
 * different thing in different domains (e.g. a "disabled" staff account is a
 * real problem to notice; a "disabled" notification template is routine), so
 * tones are looked up per domain rather than from one shared dictionary.
 */
function toneMap<T extends string>(map: Record<T, StatusTone>) {
  return (value: string): StatusTone => (map as Record<string, StatusTone>)[value] ?? 'neutral';
}

export const staffStatusTone = toneMap({
  active: 'positive',
  invited: 'attention',
  suspended: 'attention',
  disabled: 'negative',
} as const);

export const applicationLifecycleTone = toneMap({
  active: 'positive',
  closed: 'neutral',
  archived: 'neutral',
} as const);

export const documentRequestStatusTone = toneMap({
  pending: 'attention',
  uploaded: 'attention',
  under_review: 'attention',
  accepted: 'positive',
  waived: 'neutral',
  replacement_requested: 'negative',
  rejected: 'negative',
  cancelled: 'neutral',
  expired: 'negative',
} as const);

export const integrationStatusTone = toneMap({
  disconnected: 'neutral',
  pending: 'attention',
  connected: 'positive',
  needs_reauth: 'negative',
  failed: 'negative',
  disabled: 'neutral',
} as const);

export const documentUploadStatusTone = toneMap({
  quarantined: 'attention',
  available: 'attention',
  accepted: 'positive',
  rejected: 'negative',
  superseded: 'neutral',
  withdrawn: 'neutral',
} as const);

export const appointmentStatusTone = toneMap({
  pending_confirmation: 'attention',
  confirmed: 'positive',
  rescheduled: 'attention',
  cancelled: 'neutral',
  rejected: 'negative',
  completed: 'positive',
  no_show: 'negative',
} as const);

export const stageStatusTone = toneMap({
  pending: 'attention',
  complete: 'positive',
  not_applicable: 'neutral',
} as const);

export const templateActiveTone = (active: boolean): StatusTone => (active ? 'positive' : 'neutral');
export const versionPublishedTone = (published: boolean): StatusTone =>
  published ? 'positive' : 'attention';
